import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { getBannerById, updateBanner } from '../../api/bannerApi' // Added updateBanner import

const EditBanner = () => {
  const { id } = useParams()

  const navigate = useNavigate()
  const [imagePreview, setImagePreview] = useState(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      image: null,
    },
  })

  const imageField = watch('image')

  const { data: dealData, isLoading } = useQuery({
    queryKey: ['banner', id],
    queryFn: () => getBannerById(id),
    enabled: !!id, // Only run query if id exists
  })

  // Set initial image preview when data loads
  useEffect(() => {
    if (dealData?.image) {
      setImagePreview(dealData.image)
    }
  }, [dealData])

  // Update deal mutation
  const updateMutation = useMutation({
    mutationFn: (data) => updateBanner(id, data),
    onSuccess: () => {
      toast.success('Banner updated successfully!')
      navigate(-1)
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(error.response?.data?.message || 'Failed to update banner')
    },
  })

  useEffect(() => {
    if (imageField && imageField[0]) {
      const file = imageField[0]
      const previewUrl = URL.createObjectURL(file)
      setImagePreview(previewUrl)

      // Clean up the URL when component unmounts or image changes
      return () => URL.revokeObjectURL(previewUrl)
    }
  }, [imageField])

  const handleImageChange = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      setValue('image', files)
    }
  }

  const onSubmit = async (data) => {
    if (!data.image || data.image.length === 0) {
      toast.error('Please select an image before submitting.')
      return
    }
    updateMutation.mutate(data)
  }

  if (isLoading) {
    return (
      <div className="max-w-8xl text-center mx-auto px-4 py-6">
        <p className="text-gray-600">Loading data...</p>
      </div>
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white p-6 rounded-xl shadow">
          <h2 className="font-semibold text-gray-800 mb-2">Brand Logo</h2>

          {/* Show image preview if available */}
          {imagePreview && (
            <div className="mb-4">
              <img
                src={imagePreview}
                alt="Brand Logo Preview"
                className="w-full max-h-64 object-cover rounded-lg border border-gray-200"
              />
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            {...register('image', {
              required: !dealData?.image ? 'Image is required' : false, // Make optional if existing image
              validate: {
                fileSize: (files) => {
                  if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                    return 'File size must be less than 2MB'
                  }
                  return true
                },
              },
            })}
            className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100"
          />
          {errors.image && (
            <p className="text-red-500 text-sm mt-1">{errors.image.message}</p>
          )}
        </div>

        <div className="mt-8 text-center">
          <button
            type="submit"
            disabled={isSubmitting || updateMutation.isLoading}
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white text-md font-medium px-5 py-3 rounded-lg shadow-md transition-all"
          >
            {isSubmitting || updateMutation.isLoading
              ? 'Processing...'
              : 'Update'}
          </button>
        </div>
      </form>
    </>
  )
}

export default EditBanner
